// Plugin to handle <details> HTML tags directly

import type { PluginSimple } from 'markdown-it'
import type StateBlock from 'markdown-it/lib/rules_block/state_block.mjs'

type DetailsContent = {
  summaryText: string
  bodyText: string
  hasOpen: boolean
  nextLine: number
}

const parseDetailsBlock = (
  state: StateBlock,
  start: number,
  end: number,
): DetailsContent | null => {
  const max = state.eMarks[start] as number

  // Get the full line content to check for <details tag (handles indentation)
  const lineContent = state.src.slice(state.bMarks[start] as number, max)
  const detailsMatch = lineContent.match(/^(\s*)<details/)

  if (!detailsMatch) return null

  // Calculate the actual position of the <details tag
  const indentLength = detailsMatch[1]?.length ?? 0
  const detailsStartPos = (state.bMarks[start] as number) + indentLength

  // Find the end of the opening tag
  const tagEndPos = state.src.indexOf('>', detailsStartPos)
  if (tagEndPos === -1) return null

  // Check for open attribute
  const openingTag = state.src.slice(detailsStartPos, tagEndPos + 1)
  const hasOpen = openingTag.includes(' open')

  // Find closing tag using simple string search
  const closeTagPos = state.src.indexOf('</details>', tagEndPos)
  if (closeTagPos === -1) return null

  // Find what line the closing tag is on
  let nextLine = start
  while (nextLine < end) {
    const lineEnd = state.eMarks[nextLine] as number
    if (lineEnd >= closeTagPos + '</details>'.length) break
    nextLine++
  }

  // Extract content between tags - preserve whitespace for indentation
  const fullContent = state.src.slice(tagEndPos + 1, closeTagPos)

  // Parse summary and body using string methods
  const summaryStart = fullContent.indexOf('<summary>')
  if (summaryStart === -1) {
    // No summary tag found, use entire content as body
    return {
      summaryText: 'Details',
      bodyText: fullContent,
      hasOpen,
      nextLine,
    }
  }

  const summaryContentStart = summaryStart + '<summary>'.length
  const summaryEnd = fullContent.indexOf('</summary>', summaryContentStart)
  if (summaryEnd === -1) {
    // No closing summary tag found, use entire content as body
    return {
      summaryText: 'Details',
      bodyText: fullContent,
      hasOpen,
      nextLine,
    }
  }

  const summaryText = fullContent.slice(summaryContentStart, summaryEnd).trim() || 'Details'
  const bodyText = fullContent.slice(summaryEnd + '</summary>'.length)

  return { summaryText, bodyText, hasOpen, nextLine }
}

const parseBodyAsBlocks = (state: StateBlock, bodyText: string) => {
  if (!bodyText.trim()) return

  // Save essential state
  const { src, bMarks, eMarks, tShift, sCount, lineMax, line } = state

  // Normalize indentation: find minimum indent and remove it from all lines
  const lines = bodyText.split('\n')

  // Find minimum indentation among non-empty lines
  let minIndent = Infinity
  for (const line of lines) {
    if (line.trim()) {
      const match = line.match(/^[ \t]*/)
      const indent = match ? match[0].length : 0
      minIndent = Math.min(minIndent, indent)
    }
  }

  if (minIndent === Infinity) minIndent = 0

  // Remove the minimum indentation from all lines
  const normalizedLines = lines.map((line) => {
    if (!line.trim()) return line // Keep empty lines
    return line.slice(minIndent)
  })

  const normalizedContent = normalizedLines.join('\n')

  // Now parse the normalized content as regular markdown
  state.src = normalizedContent + '\n'
  const contentLines = normalizedContent.split('\n')
  state.lineMax = contentLines.length
  state.line = 0

  // Set up line markers for the normalized content
  let pos = 0
  state.bMarks = []
  state.eMarks = []
  state.tShift = []
  state.sCount = []

  for (let i = 0; i < contentLines.length; i++) {
    const line = contentLines[i] as string

    state.bMarks.push(pos)
    state.eMarks.push(pos + line.length)

    // Calculate indentation for this normalized line
    const match = line.match(/^[ \t]*/)
    const indent = match ? match[0].length : 0

    state.tShift.push(indent)
    state.sCount.push(indent)

    pos += line.length + 1
  }

  // Parse the normalized content
  state.md.block.tokenize(state, 0, contentLines.length)

  // Restore original state
  Object.assign(state, { src, bMarks, eMarks, tShift, sCount, lineMax, line })
}

export const detailsPlugin: PluginSimple = (md) => {
  md.block.ruler.before('code', 'details_block', (state, start, end, silent) => {
    const parsed = parseDetailsBlock(state, start, end)
    if (!parsed) return false

    if (silent) return true

    const { summaryText, bodyText, hasOpen, nextLine } = parsed

    // Create details tokens
    const detailsOpen = state.push('details_open', 'details', 1)
    if (hasOpen) {
      detailsOpen.attrSet('open', 'true')
    }

    // Create summary tokens
    state.push('summary_open', 'summary', 1)
    const summaryInline = state.push('inline', '', 0)
    summaryInline.content = summaryText
    summaryInline.children = []
    state.push('summary_close', 'summary', -1)

    // Parse body content as blocks
    parseBodyAsBlocks(state, bodyText)

    state.push('details_close', 'details', -1)
    state.line = nextLine + 1
    return true
  })

  // Add renderer rules for details and summary
  md.renderer.rules.details_open = (tokens, idx) => {
    const token = tokens[idx]
    if (!token) return '<details>'
    const openAttr = token.attrGet('open') ? ' open' : ''
    return `<details${openAttr}>`
  }

  md.renderer.rules.details_close = () => '</details>'
  md.renderer.rules.summary_open = () => '<summary>'
  md.renderer.rules.summary_close = () => '</summary>'
}
